import { useCallback, useEffect, useRef, useState } from "react";
import { useAction, useMutation, useQuery } from "convex/react";

import { useNavigate } from "@tanstack/react-router";
import { api } from "convex/_generated/api";
import type { Id } from "convex/_generated/dataModel";
import { useTranslation } from "@/packages/utils";
import { cn } from "@/packages/utils";
import { toast } from "sonner";
import { Check, Download } from "lucide-react";
import { motion } from "framer-motion";
import { springs } from "@/components/ui/motion";
import { Button, ConfirmDeleteDialog } from "@/components/ui";

import {
  SettingsPageHeader,
  SettingsPanel,
  SettingsSection,
} from "./SettingsSection";
import { WorkspaceSettingsForm } from "./WorkspaceSettingsForm";
import { WorkspaceSelector } from "./WorkspaceSelector";
import { MemberManagement } from "./MemberManagement";
import { OrgContextSettingsSection } from "./OrgContextSettingsSection";
import type { OrgContextAnswers } from "@/components/workspace/OrgContextForm";

type ProfilingCompanySize = "1-20" | "21-50" | "51-200" | "201-500";

const COMPANY_SIZE_OPTIONS: ProfilingCompanySize[] = [
  "1-20",
  "21-50",
  "51-200",
  "201-500",
];

interface WorkspaceSettingsPageProps {
  workspace: {
    _id: Id<"workspaces">;
    name: string;
    slug: string;
    role: string;
    industry: string;
    companySize: string;
    jurisdiction: string;
    orgContext?: OrgContextAnswers;
    profilingCompanySize?: ProfilingCompanySize;
  };
}

export function WorkspaceSettingsPage({
  workspace,
}: WorkspaceSettingsPageProps) {
  const isOwner = workspace.role === "owner";

  const { t } = useTranslation(["settings"]);

  return (
    <>
      <WorkspaceSelector currentSlug={workspace.slug} />

      <div className="border-t border-border mb-8" />

      <section id="general" className="scroll-mt-4">
        <SettingsPageHeader
          eyebrow={t("settings:sections.organization")}
          title={t("settings:workspace.title")}
          description={t("settings:workspace.description")}
        />

        <div className="mt-8" />

        <WorkspaceSettingsForm workspace={workspace} />
      </section>

      <div className="border-t border-border my-8" />

      <section id="members" className="scroll-mt-4">
        <MemberManagement
          workspaceId={workspace._id}
          currentUserRole={workspace.role}
        />
      </section>

      <div className="border-t border-border my-8" />

      <section id="org-context" className="scroll-mt-4">
        <OrgContextSettingsSection
          workspaceId={workspace._id}
          orgContext={workspace.orgContext}
          isReadOnly={workspace.role === "member"}
        />
      </section>

      <div className="border-t border-border my-8" />

      <section id="audit-defaults" className="scroll-mt-4">
        <AuditDefaultsSection
          workspaceId={workspace._id}
          profilingCompanySize={workspace.profilingCompanySize}
          isReadOnly={workspace.role === "member"}
        />
      </section>

      {isOwner && (
        <>
          <div className="border-t border-border my-8" />
          <section id="manage" className="scroll-mt-4">
            <ManageWorkspaceSection workspace={workspace} />
          </section>
        </>
      )}
    </>
  );
}

// ── Audit Defaults ─────────────────────────────────────────────────

const COMPANY_SIZE_LABEL_KEYS = {
  "1-20": "audits:wizard.profiling.companySize.options.1-20",
  "21-50": "audits:wizard.profiling.companySize.options.21-50",
  "51-200": "audits:wizard.profiling.companySize.options.51-200",
  "201-500": "audits:wizard.profiling.companySize.options.201-500",
} as const;

function AuditDefaultsSection({
  workspaceId,
  profilingCompanySize,
  isReadOnly,
}: {
  workspaceId: Id<"workspaces">;
  profilingCompanySize?: ProfilingCompanySize;
  isReadOnly: boolean;
}) {
  const { t } = useTranslation(["settings", "audits"]);
  const updateCompanySize = useMutation(
    api.workspaces.updateWorkspaceProfilingCompanySize,
  );

  async function handleSelect(size: ProfilingCompanySize) {
    if (isReadOnly) return;
    try {
      await updateCompanySize({ workspaceId, profilingCompanySize: size });
      toast.success(t("settings:workspace.auditDefaults.saved"));
    } catch {
      toast.error(t("settings:workspace.auditDefaults.saveFailed"));
    }
  }

  return (
    <SettingsSection
      title={t("settings:workspace.auditDefaults.title")}
      description={t("settings:workspace.auditDefaults.description")}
    >
      <div className="max-w-md space-y-2">
        <span className="text-[12px] font-medium text-primary block">
          {t("settings:workspace.auditDefaults.companySizeLabel")}
        </span>
        <div className="grid gap-1.5">
          {COMPANY_SIZE_OPTIONS.map((opt) => {
            const isSelected = profilingCompanySize === opt;
            return (
              <button
                key={opt}
                type="button"
                onClick={() => handleSelect(opt)}
                disabled={isReadOnly}
                className={cn(
                  "flex items-center gap-2.5 rounded-lg border px-3 py-2 text-left text-[12px] transition-all duration-150",
                  isSelected
                    ? "border-accent bg-accent/5 text-accent font-medium"
                    : "border-border hover:border-accent/40 text-primary",
                  isReadOnly && "opacity-50 cursor-not-allowed",
                )}
              >
                <div
                  className={cn(
                    "flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 transition-colors",
                    isSelected ? "border-accent bg-accent" : "border-border",
                  )}
                >
                  {isSelected && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={springs.bouncy}
                    >
                      <Check className="h-2.5 w-2.5 text-white" />
                    </motion.div>
                  )}
                </div>
                {t(COMPANY_SIZE_LABEL_KEYS[opt])}
              </button>
            );
          })}
        </div>
      </div>
    </SettingsSection>
  );
}

// ── Workspace Management (formerly Danger Zone) ──────────────────

function ManageWorkspaceSection({
  workspace,
}: {
  workspace: { _id: Id<"workspaces">; name: string };
}) {
  const { t } = useTranslation(["settings", "common"]);
  const deleteWorkspace = useAction(
    api.workspace_action_delete.deleteWorkspace,
  );
  const navigate = useNavigate();
  const [showConfirm, setShowConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [exportRequested, setExportRequested] = useState(false);

  const exportData = useQuery(
    api.workspace_export.exportWorkspaceData,
    exportRequested ? { workspaceId: workspace._id } : "skip",
  );

  // Track whether we already triggered the download for this request
  const downloadTriggeredRef = useRef(false);

  const handleExport = useCallback(() => {
    if (exportData) {
      triggerJsonDownload(exportData, `${workspace.name}-export.json`);
      return;
    }
    downloadTriggeredRef.current = false;
    setExportRequested(true);
  }, [exportData, workspace.name]);

  const exportPending = exportRequested && !exportData;

  // When export data arrives after request, trigger download
  useEffect(() => {
    if (exportRequested && exportData && !downloadTriggeredRef.current) {
      downloadTriggeredRef.current = true;
      triggerJsonDownload(exportData, `${workspace.name}-export.json`);
      toast.success(t("dangerZone.exportDownloaded") as string);
    }
  }, [exportRequested, exportData, workspace.name, t]);

  async function handleDelete() {
    setDeleting(true);
    try {
      await deleteWorkspace({ workspaceId: workspace._id });
      toast.success(t("dangerZone.workspaceDeleted") as string);
      navigate({ to: "/" });
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : (t("dangerZone.failedDeleteWorkspace") as string),
      );
      setDeleting(false);
    }
  }

  return (
    <SettingsPanel
      title={t("dangerZone.title") as string}
      description={t("dangerZone.description") as string}
      tone="danger"
      contentClassName="space-y-4"
    >
      <ManageRow
        title={t("dangerZone.exportData.title") as string}
        description={t("dangerZone.exportData.description") as string}
        action={
          <Button
            variant="secondary"
            onClick={handleExport}
            disabled={deleting || exportPending}
            loading={exportPending}
          >
            <Download className="h-4 w-4 mr-1.5" />
            {t("dangerZone.exportData.button")}
          </Button>
        }
      />

      <ManageRow
        title={t("dangerZone.deleteWorkspace.title") as string}
        description={t("dangerZone.deleteWorkspace.description") as string}
        action={
          <Button
            variant="danger"
            onClick={() => setShowConfirm(true)}
            disabled={deleting}
          >
            {t("dangerZone.deleteWorkspace.button")}
          </Button>
        }
      />

      <ConfirmDeleteDialog
        open={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={handleDelete}
        loading={deleting}
        title={t("dangerZone.deleteWorkspace.confirmTitle")}
        description={t(
          "settings:dangerZone.deleteWorkspace.confirmDescription",
          {
            workspaceName: workspace.name,
          },
        )}
        confirmMatchText={workspace.name}
        confirmLabel={t("dangerZone.deleteWorkspace.confirmButton")}
      />
    </SettingsPanel>
  );
}

function ManageRow({
  title,
  description,
  action,
}: {
  title: string;
  description: string;
  action: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-4 rounded-[18px] border border-red-100 bg-white/75 p-4 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <p className="text-[14px] font-semibold text-primary">{title}</p>
        <p className="mt-1 text-[13px] leading-6 text-secondary">
          {description}
        </p>
      </div>
      <div className="shrink-0">{action}</div>
    </div>
  );
}

// ── Helpers ────────────────────────────────────────────────────────

function triggerJsonDownload(data: unknown, filename: string) {
  const json = JSON.stringify(data, null, 2);
  const blob = new Blob([json], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
